<script setup lang="ts">
import { computed, ref } from 'vue'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@collector/shared-ui'
import { ParticipantLogo } from '@collector/shared-ui-domain'
import type { Participant, SubParticipant } from '@collector/sportsapi-types'

import type { SportsStatConfig } from '../../../interfaces/SportStatConfig'
import PlayerStatisticsTable from './PlayerStatisticsTable.vue'

type Props = {
  playerStats: SportsStatConfig[]
  homeParticipant: Participant
  awayParticipant: Participant
  initialHomePlayers: SubParticipant[]
  initialAwayPlayers: SubParticipant[]
  teamColorsSwitched?: boolean
}

defineProps<Props>()

// state
const activeTab = ref('home')
const homePlayersDirty = defineModel<boolean>('homePlayersDirty', {
  default: false,
})
const awayPlayersDirty = defineModel<boolean>('awayPlayersDirty', {
  default: false,
})
const homePlayers = defineModel<SubParticipant[]>('homePlayers', {
  required: true,
})
const awayPlayers = defineModel<SubParticipant[]>('awayPlayers', {
  required: true,
})

// computed
const indicatorClass = computed(() => {
  const bg =
    activeTab.value === 'home'
      ? 'bg-current-team-home-5'
      : 'bg-current-team-away-5'
  return `${bg} transition-colors`
})
</script>

<template>
  <Tabs
    v-model="activeTab"
    class="gap-2"
  >
    <TabsList
      class="grid w-full grid-cols-2"
      :indicatorClass
    >
      <TabsTrigger
        class="flex items-center justify-center gap-2 transition-colors"
        :class="{
          'text-neutral-light-10': activeTab === 'home' && teamColorsSwitched,
        }"
        value="home"
      >
        <ParticipantLogo
          class="-my-2"
          :participant="homeParticipant"
        />
        <div class="-mb-1 truncate pb-1">{{ homeParticipant.short_name }}</div>
      </TabsTrigger>
      <TabsTrigger
        class="flex items-center justify-center gap-2 transition-colors"
        :class="{
          'text-neutral-light-10': activeTab === 'away' && !teamColorsSwitched,
        }"
        value="away"
      >
        <div class="-mb-1 truncate pb-1">
          {{ awayParticipant.short_name }}
        </div>
        <ParticipantLogo
          class="-my-2"
          :participant="awayParticipant"
        />
      </TabsTrigger>
    </TabsList>
    <TabsContent
      value="home"
      forceMount
    >
      <PlayerStatisticsTable
        v-model:players="homePlayers"
        v-model:dirty="homePlayersDirty"
        :initialPlayers="initialHomePlayers"
        :playerStats
      />
    </TabsContent>
    <TabsContent
      value="away"
      forceMount
    >
      <PlayerStatisticsTable
        v-model:players="awayPlayers"
        v-model:dirty="awayPlayersDirty"
        :initialPlayers="initialAwayPlayers"
        :playerStats
      />
    </TabsContent>
  </Tabs>
</template>
